import React, { useState, useCallback, useEffect } from 'react';
import { Search, UserPlus, User, Phone, Mail, Ticket, ChevronRight, ArrowLeft, ShoppingCart, Plus, Minus, Trash2, Crown, Cake, Award, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import api from '../../../utils/apiClient';

// Common country codes with Fiji as default
const COUNTRY_CODES = [
  { code: '+679', country: 'Fiji', flag: '🇫🇯' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
];

export default function ClientLookup({ authHeaders }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  
  // Add contact form
  const [newContact, setNewContact] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country_code: '+679',
    country_name: 'Fiji'
  });
  const [creating, setCreating] = useState(false);
  
  // Assign code
  const [assigningCode, setAssigningCode] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(10);

  // Order state
  const [menuItems, setMenuItems] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [orderDiscountCode, setOrderDiscountCode] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [orderType, setOrderType] = useState('walk_in_takeaway'); // dine_in or walk_in_takeaway
  const [submittingOrder, setSubmittingOrder] = useState(false);

  // Fetch menu items when order modal opens
  useEffect(() => {
    if (showOrderModal) {
      fetchMenuItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOrderModal]);

  const fetchMenuItems = async () => {
    try {
      const res = await api.get('/staff/menu');
      setMenuItems(res.data.items || []);
    } catch (err) {
      console.error('Failed to fetch menu');
    }
  };

  // Helper to detect if query looks like a phone number
  const isPhoneNumber = (query) => {
    const cleaned = query.replace(/[\s\-\(\)\.]/g, '');
    // Check if it's mostly digits (with optional + at start)
    return /^\+?\d{6,15}$/.test(cleaned);
  };

  // Helper to extract and format phone number from search query
  const extractPhoneFromQuery = (query) => {
    const cleaned = query.replace(/[\s\-\(\)\.]/g, '');
    // Check for country code
    for (const cc of COUNTRY_CODES) {
      if (cleaned.startsWith(cc.code.replace('+', ''))) {
        return {
          country_code: cc.code,
          country_name: cc.country,
          phone: cleaned.substring(cc.code.length - 1) // Remove country code digits
        };
      }
      if (cleaned.startsWith(cc.code)) {
        return {
          country_code: cc.code,
          country_name: cc.country,
          phone: cleaned.substring(cc.code.length)
        };
      }
    }
    // Default to Fiji if no country code detected
    return {
      country_code: '+679',
      country_name: 'Fiji',
      phone: cleaned.replace(/^\+/, '')
    };
  };

  const searchContacts = useCallback(async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    
    setSearching(true);
    setSelectedContact(null);
    try {
      const res = await api.get(`/staff/lookup?query=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data.contacts || []);
      // Don't show toast - we'll show inline message instead
    } catch (err) {
      toast.error('Search failed');
    }
    setSearching(false);
  }, [searchQuery]);

  const resetNewContactForm = () => {
    setNewContact({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      country_code: '+679',
      country_name: 'Fiji'
    });
  };

  // Pre-populate phone number and open add modal
  const handleCreateWithPhone = () => {
    if (isPhoneNumber(searchQuery)) {
      const phoneData = extractPhoneFromQuery(searchQuery);
      setNewContact({
        first_name: '',
        last_name: '',
        email: '',
        phone: phoneData.phone,
        country_code: phoneData.country_code,
        country_name: phoneData.country_name
      });
    } else {
      resetNewContactForm();
    }
    setShowAddModal(true);
  };

  const createContact = async () => {
    if (!newContact.first_name || !newContact.last_name) {
      toast.error('First name and last name are required');
      return;
    }
    if (!newContact.email && !newContact.phone) {
      toast.error('Email or phone number is required');
      return;
    }
    
    setCreating(true);
    try {
      const res = await api.post('/staff/contacts', newContact);
      const data = res.data;
      
      if (data.is_existing) {
        toast.info('Contact already exists in database');
      } else {
        toast.success('Contact created successfully');
      }
      fetchContactDetails(data.contact_id);
      setShowAddModal(false);
      resetNewContactForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create contact');
    }
    setCreating(false);
  };

  const fetchContactDetails = async (contactId) => {
    try {
      const res = await api.get(`/staff/contacts/${contactId}`);
      setSelectedContact(res.data);
    } catch (err) {
      toast.error('Failed to fetch contact details');
    }
  };

  const assignCode = async () => {
    if (!selectedContact) return;
    
    setAssigningCode(true);
    try {
      const res = await api.post('/staff/assign-code', {
        contact_id: selectedContact.contact_id,
        discount_percent: selectedDiscount
      });
      
      const data = res.data;
      const notifMsg = [];
      if (data.notifications?.sms?.success) notifMsg.push('SMS sent');
      if (data.notifications?.email?.success) notifMsg.push('Email sent');
      
      toast.success(`Code ${data.code} assigned (${data.discount_percent}% off)${notifMsg.length ? ` - ${notifMsg.join(', ')}` : ''}`);
      fetchContactDetails(selectedContact.contact_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign code');
    }
    setAssigningCode(false);
  };

  // Order functions
  const addItemToOrder = (item) => {
    const existing = orderItems.find(i => i.item_id === item.id);
    if (existing) {
      setOrderItems(orderItems.map(i => 
        i.item_id === item.id ? { ...i, quantity: i.quantity + 1 } : i
      ));
    } else {
      setOrderItems([...orderItems, {
        item_id: item.id,
        name: item.name,
        price: item.price,
        quantity: 1
      }]);
    }
  };

  const updateItemQuantity = (itemId, delta) => {
    setOrderItems(orderItems.map(i => {
      if (i.item_id === itemId) {
        const newQty = i.quantity + delta;
        return newQty > 0 ? { ...i, quantity: newQty } : null;
      }
      return i;
    }).filter(Boolean));
  };

  const removeItemFromOrder = (itemId) => {
    setOrderItems(orderItems.filter(i => i.item_id !== itemId));
  };

  const calculateSubtotal = () => {
    return orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateDiscount = () => {
    if (!orderDiscountCode || !selectedContact) return 0;
    
    // Check available codes for discount percentage
    const allCodes = [
      ...(selectedContact.manual_codes || []),
      ...(selectedContact.campaign_signups || []).map(s => ({
        code: s.coupon_code,
        discount_percent: s.discount_percent || 10,
        used: s.coupon_used
      }))
    ];
    
    const matchedCode = allCodes.find(c => c.code === orderDiscountCode && !c.used);
    if (matchedCode) {
      return (calculateSubtotal() * (matchedCode.discount_percent || 10)) / 100;
    }
    return 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount();
  };

  const submitOrder = async () => {
    if (orderItems.length === 0) {
      toast.error('Please add items to the order');
      return;
    }
    
    setSubmittingOrder(true);
    try {
      const res = await api.post('/staff/orders', {
        contact_id: selectedContact.contact_id,
        items: orderItems,
        subtotal: calculateSubtotal(),
        discount_code: orderDiscountCode || null,
        discount_amount: calculateDiscount(),
        total: calculateTotal(),
        payment_method: paymentMethod,
        order_type: orderType,
        notes: orderNotes || null
      });
      
      toast.success(`Order created! Total: $${res.data.total.toFixed(2)}`);
      setShowOrderModal(false);
      setOrderItems([]);
      setOrderDiscountCode('');
      setOrderNotes('');
      setPaymentMethod('cash');
      setOrderType('walk_in_takeaway');
      // Refresh contact to show updated order history
      fetchContactDetails(selectedContact.contact_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    }
    setSubmittingOrder(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  // Get available discount codes for the selected contact
  const getAvailableCodes = () => {
    if (!selectedContact) return [];
    
    const codes = [];
    
    // Manual codes
    (selectedContact.manual_codes || []).forEach(c => {
      if (!c.used) {
        codes.push({ code: c.code, discount_percent: c.discount_percent, source: 'Staff' });
      }
    });
    
    // Campaign codes
    (selectedContact.campaign_signups || []).forEach(s => {
      if (!s.coupon_used) {
        codes.push({ code: s.coupon_code, discount_percent: s.discount_percent || 10, source: s.campaign_name });
      }
    });
    
    return codes;
  };

  return (
    <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden" data-testid="client-lookup-section">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Search className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-heading text-lg text-foreground">Client Lookup</h2>
            <p className="text-xs text-muted-foreground">Search walk-in customers by name, phone, or email</p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleCreateWithPhone}
          data-testid="add-new-customer-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add New
        </Button>
      </div>

      {/* Search Section */}
      <div className="p-4">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by first name, last name, phone (+679...), or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchContacts()}
              className="pl-10"
              data-testid="client-search-input"
            />
          </div>
          <Button 
            onClick={searchContacts} 
            disabled={searching}
            data-testid="client-search-btn"
          >
            {searching ? 'Searching...' : 'Search'}
          </Button>
        </div>
      </div>

      {/* Results or Contact Details */}
      {selectedContact ? (
        /* Contact Details View */
        <div className="border-t border-border">
          <div className="p-4 bg-muted/20">
            <div className="flex items-center justify-between mb-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedContact(null)}
                data-testid="back-to-results-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Results
              </Button>
              <Button
                size="sm"
                onClick={() => setShowOrderModal(true)}
                data-testid="take-order-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Take Order
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Contact Info */}
              <div className="bg-card rounded-xl p-4 border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-lg text-foreground" data-testid="contact-name">
                        {selectedContact.name || `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim()}
                      </h3>
                      {selectedContact.is_founding_member && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 flex items-center gap-1">
                          <Crown className="w-3 h-3" />
                          Founding #{selectedContact.founding_member_number || ''}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedContact.tier && selectedContact.tier.id !== 'new' && (
                        <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${
                          selectedContact.tier.id === 'platinum' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                          selectedContact.tier.id === 'gold' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                          selectedContact.tier.id === 'silver' ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' :
                          'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                        }`}>
                          <Award className="w-3 h-3" />
                          {selectedContact.tier.name} ({selectedContact.tier.discount}% off)
                        </span>
                      )}
                      {(selectedContact.tags || []).slice(0, 2).map(tag => (
                        <span key={tag} className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="text-foreground">{selectedContact.email || 'No email'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span className="text-foreground">{selectedContact.phone || 'No phone'}</span>
                  </div>
                  <div className="pt-2 border-t border-border mt-2 grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-muted-foreground text-xs">First Contact</span>
                      <p className="font-medium">{formatDate(selectedContact.first_contact_date)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs">Total Spent</span>
                      <p className="font-medium">${(selectedContact.total_spent || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs">Total Orders</span>
                      <p className="font-medium">{selectedContact.total_orders || 0}</p>
                    </div>
                    {selectedContact.loyalty_stats && (
                      <div>
                        <span className="text-muted-foreground text-xs">Free Coffees</span>
                        <p className="font-medium text-primary">{selectedContact.loyalty_stats.free_coffees_available || 0} available</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Birthday & Rewards Section */}
              <div className="space-y-4">
                {/* Birthday Coffee */}
                {selectedContact.birthday && (
                  <div className={`rounded-xl p-4 border ${
                    selectedContact.birthday.is_today 
                      ? 'bg-pink-50 dark:bg-pink-900/20 border-pink-200 dark:border-pink-800' 
                      : 'bg-card border-border'
                  }`}>
                    <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                      <Cake className={`w-4 h-4 ${selectedContact.birthday.is_today ? 'text-pink-500' : ''}`} />
                      Birthday Reward
                      {selectedContact.birthday.is_today && (
                        <span className="text-xs bg-pink-500 text-white px-2 py-0.5 rounded-full animate-pulse">TODAY!</span>
                      )}
                    </h4>
                    
                    <div className="space-y-2 text-sm">
                      <p className="text-muted-foreground">
                        Birthday: {new Date(selectedContact.birthday.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                      </p>
                      
                      {selectedContact.birthday.is_today && selectedContact.birthday.coffee_available && (
                        <Button
                          onClick={async () => {
                            try {
                              const res = await api.post(`/loyalty/staff/redeem-birthday-coffee/${selectedContact.email}`);
                              toast.success(res.data.message);
                              fetchContactDetails(selectedContact.contact_id);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to redeem birthday coffee');
                            }
                          }}
                          className="w-full bg-pink-500 hover:bg-pink-600"
                          size="sm"
                          data-testid="redeem-birthday-btn"
                        >
                          <Gift className="w-4 h-4 mr-2" />
                          Give Free Birthday Coffee
                        </Button>
                      )}
                      
                      {selectedContact.birthday.coffee_redeemed && (
                        <p className="text-green-600 text-sm">✓ Birthday coffee already redeemed this year</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Assign Discount Code */}
                <div className="bg-card rounded-xl p-4 border border-border">
                  <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <Ticket className="w-4 h-4" />
                    Assign Discount Code
                  </h4>
                  
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Discount Percentage (Max 15%)</Label>
                      <select
                        value={selectedDiscount}
                        onChange={(e) => setSelectedDiscount(parseInt(e.target.value))}
                        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                        data-testid="discount-select"
                      >
                        <option value={5}>5% off</option>
                        <option value={10}>10% off</option>
                        <option value={15}>15% off</option>
                      </select>
                    </div>
                    
                    <Button
                      onClick={assignCode}
                      disabled={assigningCode}
                      className="w-full"
                      size="sm"
                      data-testid="assign-code-btn"
                    >
                      {assigningCode ? 'Assigning...' : 'Assign Code (SMS + Email)'}
                    </Button>
                  </div>
                
                  {/* Existing Codes */}
                  {(selectedContact.manual_codes?.length > 0 || selectedContact.campaign_signups?.length > 0) && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <h5 className="text-xs font-medium text-muted-foreground mb-2">Existing Codes</h5>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {(selectedContact.manual_codes || []).map((code, i) => (
                          <div key={`manual-${i}`} className="flex items-center justify-between p-2 bg-muted rounded-lg text-xs">
                            <span className="font-mono font-bold text-primary">{code.code}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">{code.discount_percent}%</span>
                              <span className={`px-1.5 py-0.5 rounded ${code.used ? 'bg-gray-500/10 text-gray-500' : 'bg-green-500/10 text-green-500'}`}>
                                {code.used ? 'Used' : 'Active'}
                              </span>
                            </div>
                          </div>
                        ))}
                        {(selectedContact.campaign_signups || []).map((signup, i) => (
                          <div key={`campaign-${i}`} className="flex items-center justify-between p-2 bg-muted rounded-lg text-xs">
                            <div>
                              <span className="font-mono font-bold text-primary">{signup.coupon_code}</span>
                              <span className="text-muted-foreground ml-1">({signup.campaign_name})</span>
                            </div>
                            <span className={`px-1.5 py-0.5 rounded ${signup.coupon_used ? 'bg-gray-500/10 text-gray-500' : 'bg-green-500/10 text-green-500'}`}>
                              {signup.coupon_used ? 'Used' : 'Active'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Order History */}
            {selectedContact.orders && selectedContact.orders.length > 0 && (
              <div className="mt-4 bg-card rounded-xl p-4 border border-border">
                <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  Order History ({selectedContact.orders.length})
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedContact.orders.slice().reverse().map((order) => (
                    <div key={`order-${order.order_date}-${order.total}`} className="flex items-center justify-between p-2 bg-muted rounded-lg text-sm">
                      <div>
                        <span className="font-medium">{formatDate(order.order_date)}</span>
                        <span className="text-muted-foreground ml-2">{order.items_count} items</span>
                        {order.coupon_used && (
                          <span className="text-xs text-primary ml-2">Code: {order.coupon_used}</span>
                        )}
                      </div>
                      <span className="font-bold">${order.total?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : searchResults.length > 0 ? (
        /* Search Results */
        <div className="border-t border-border divide-y divide-border max-h-80 overflow-y-auto">
          {searchResults.map((contact) => (
            <div
              key={contact.contact_id}
              className="p-3 hover:bg-muted/30 cursor-pointer transition-colors flex items-center justify-between"
              onClick={() => fetchContactDetails(contact.contact_id)}
              data-testid={`search-result-${contact.contact_id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="font-medium text-foreground">
                    {contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown'}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {contact.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {contact.email}
                      </span>
                    )}
                    {contact.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {contact.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {contact.assigned_codes && contact.assigned_codes.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/10 text-green-500">
                    {contact.assigned_codes.length} code{contact.assigned_codes.length !== 1 ? 's' : ''}
                  </span>
                )}
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          ))}
        </div>
      ) : searchQuery && !searching ? (
        /* No Results - Enhanced UX with phone number pre-population */
        <div className="p-6 text-center border-t border-border">
          <div className="w-14 h-14 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mx-auto mb-4">
            <Phone className="w-7 h-7 text-orange-600 dark:text-orange-400" />
          </div>
          
          {isPhoneNumber(searchQuery) ? (
            <>
              <h3 className="font-semibold text-foreground mb-2">Phone Number Not Found</h3>
              <p className="text-muted-foreground mb-1">
                No account exists for:
              </p>
              <p className="font-mono text-lg font-bold text-foreground mb-4 bg-muted px-3 py-1.5 rounded-lg inline-block">
                {searchQuery}
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Would you like to create a new account with this phone number?
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button onClick={handleCreateWithPhone} data-testid="create-with-phone-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Yes, Create Account
                </Button>
                <Button variant="outline" onClick={() => setSearchQuery('')}>
                  Search Again
                </Button>
              </div>
            </>
          ) : (
            <>
              <h3 className="font-semibold text-foreground mb-2">No Contacts Found</h3>
              <p className="text-muted-foreground mb-4">
                No results for "<span className="font-medium text-foreground">{searchQuery}</span>"
              </p>
              <Button size="sm" onClick={handleCreateWithPhone}>
                <UserPlus className="w-4 h-4 mr-2" />
                Add New Customer
              </Button>
            </>
          )}
        </div>
      ) : null}

      {/* Add New Customer Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => { setShowAddModal(open); if (!open) resetNewContactForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Add New Walk-In Customer
            </DialogTitle>
            <DialogDescription>
              Enter customer details to create a new contact record.
            </DialogDescription>
          </DialogHeader>
          
          {/* Phone pre-populated notice */}
          {newContact.phone && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 flex items-start gap-2">
              <Phone className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-green-800 dark:text-green-300">Phone number pre-filled</p>
                <p className="text-green-700 dark:text-green-400">
                  Creating account for: <span className="font-mono font-bold">{newContact.country_code} {newContact.phone}</span>
                </p>
              </div>
            </div>
          )}
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">First Name *</Label>
                <Input
                  placeholder="First name"
                  value={newContact.first_name}
                  onChange={(e) => setNewContact({...newContact, first_name: e.target.value})}
                  data-testid="new-contact-first-name"
                  autoFocus
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Last Name *</Label>
                <Input
                  placeholder="Last name"
                  value={newContact.last_name}
                  onChange={(e) => setNewContact({...newContact, last_name: e.target.value})}
                  data-testid="new-contact-last-name"
                />
              </div>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="customer@email.com"
                  value={newContact.email}
                  onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                  className="pl-10"
                  data-testid="new-contact-email"
                />
              </div>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs">Phone Number (Default: Fiji +679)</Label>
              <div className="flex gap-2">
                <select
                  value={newContact.country_code}
                  onChange={(e) => {
                    const selected = COUNTRY_CODES.find(c => c.code === e.target.value);
                    setNewContact({
                      ...newContact,
                      country_code: e.target.value,
                      country_name: selected?.country || ''
                    });
                  }}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm w-28"
                  data-testid="new-contact-country-code"
                >
                  {COUNTRY_CODES.map(c => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.code}
                    </option>
                  ))}
                </select>
                <div className="flex-1 relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="tel"
                    placeholder="Phone number"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                    className="pl-10"
                    data-testid="new-contact-phone"
                  />
                </div>
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground">
              * First and last name required. At least one of email or phone must be provided.
            </p>
            
            <div className="flex gap-2 pt-2">
              <Button
                onClick={createContact}
                disabled={creating}
                className="flex-1"
                data-testid="create-contact-btn"
              >
                {creating ? 'Creating...' : 'Create Contact'}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setShowAddModal(false); resetNewContactForm(); }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Take Order Modal */}
      <Dialog open={showOrderModal} onOpenChange={setShowOrderModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Take Order for {selectedContact?.name || `${selectedContact?.first_name || ''} ${selectedContact?.last_name || ''}`.trim()}
            </DialogTitle>
            <DialogDescription>
              Add items to create an in-store order for this customer.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid md:grid-cols-2 gap-6 mt-4">
            {/* Menu Items */}
            <div>
              <h4 className="font-medium mb-3">Menu Items</h4>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {menuItems.map(item => (
                  <div 
                    key={item.id} 
                    className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 cursor-pointer"
                    onClick={() => addItemToOrder(item)}
                    data-testid={`menu-item-${item.id}`}
                  >
                    <div>
                      <p className="font-medium text-sm">{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.category}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-primary">${item.price.toFixed(2)}</span>
                      <Plus className="w-4 h-4 text-primary" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Summary */}
            <div>
              <h4 className="font-medium mb-3">Order Summary</h4>
              
              {orderItems.length === 0 ? (
                <div className="text-center p-8 bg-muted rounded-lg">
                  <ShoppingCart className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">Click menu items to add</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Order Items */}
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {orderItems.map(item => (
                      <div key={item.item_id} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{item.name}</p>
                          <p className="text-xs text-muted-foreground">${item.price.toFixed(2)} each</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0"
                            onClick={() => updateItemQuantity(item.item_id, -1)}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="w-6 text-center font-medium">{item.quantity}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0"
                            onClick={() => updateItemQuantity(item.item_id, 1)}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0 text-destructive"
                            onClick={() => removeItemFromOrder(item.item_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Discount Code */}
                  {getAvailableCodes().length > 0 && (
                    <div className="space-y-1">
                      <Label className="text-xs">Apply Discount Code</Label>
                      <select
                        value={orderDiscountCode}
                        onChange={(e) => setOrderDiscountCode(e.target.value)}
                        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                        data-testid="order-discount-select"
                      >
                        <option value="">No discount</option>
                        {getAvailableCodes().map(c => (
                          <option key={c.code} value={c.code}>
                            {c.code} ({c.discount_percent}% off - {c.source})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Payment Method */}
                  <div className="space-y-1">
                    <Label className="text-xs">Payment Method</Label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                      data-testid="payment-method-select"
                    >
                      <option value="cash">Cash</option>
                      <option value="card">Card</option>
                      <option value="pending">Pay Later</option>
                    </select>
                  </div>

                  {/* Order Type */}
                  <div className="space-y-1">
                    <Label className="text-xs">Order Type</Label>
                    <select
                      value={orderType}
                      onChange={(e) => setOrderType(e.target.value)}
                      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                      data-testid="order-type-select"
                    >
                      <option value="walk_in_takeaway">Walk-In - Take Away</option>
                      <option value="dine_in">Dine In</option>
                    </select>
                  </div>

                  {/* Notes */}
                  <div className="space-y-1">
                    <Label className="text-xs">Notes (optional)</Label>
                    <Input
                      placeholder="Order notes..."
                      value={orderNotes}
                      onChange={(e) => setOrderNotes(e.target.value)}
                      data-testid="order-notes"
                    />
                  </div>

                  {/* Totals */}
                  <div className="border-t border-border pt-3 space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal</span>
                      <span>${calculateSubtotal().toFixed(2)}</span>
                    </div>
                    {calculateDiscount() > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Discount</span>
                        <span>-${calculateDiscount().toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span>${calculateTotal().toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Submit */}
                  <Button
                    onClick={submitOrder}
                    disabled={submittingOrder || orderItems.length === 0}
                    className="w-full"
                    data-testid="submit-order-btn"
                  >
                    {submittingOrder ? 'Creating Order...' : `Complete Order - $${calculateTotal().toFixed(2)}`}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
